import classNames from 'classnames';
import { inject, observer } from 'mobx-react';
import { uniqueId } from 'lodash';
import PropTypes from 'prop-types';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { Col, FormFeedback, FormGroup, Input, Row } from 'reactstrap';

import FormModel from '../../../../models/Form';
import { modelOf } from '../../../../prop-types';
import CheckoutStore from '../../../../store/CheckoutStore';
import FormFieldId from '../../../../types/FormFieldId';
import FormFieldType from '../../../../types/FormFieldType';
import { labels } from '../../../../util/formLabels';
import globalTranslations from '../../../../i18n/globalTranslations';
import FloatingLabelWrapper from '../../../form/FloatingLabelWrapper';
import Switch from '../../../common/Switch';
import { getManualDraftFieldValue } from '../manualDeliveryDraftValidation';

const messages = defineMessages({
  phonePlaceholder: {
    id: 'checkout.deliveryForm.phonePlaceholder',
    defaultMessage: "Leave blank if same as the buyer's number",
  },
});

const getDeliveryFormLabels = () => ({
  [FormFieldId.DELIVERY_COMPANY]: labels.deliveryCompany,
  [FormFieldId.DELIVERY_FIRST_NAME]: labels.firstName,
  [FormFieldId.DELIVERY_LAST_NAME]: labels.lastName,
  [FormFieldId.DELIVERY_STREET_ADDRESS]: labels.deliveryStreetAddress,
  [FormFieldId.DELIVERY_POSTAL_CODE]: labels.deliveryPostalCode,
  [FormFieldId.DELIVERY_CITY]: labels.deliveryCity,
  [FormFieldId.DELIVERY_COUNTRY]: labels.country,
  [FormFieldId.DELIVERY_PHONE]: labels.deliveryTelephone,
  [FormFieldId.DELIVERY_CARE_OF]: labels.deliveryCareOf,
  [FormFieldId.DELIVERY_ADDRESS_DROPDOWN]: labels.deliveryAddressId,
});

const DELIVERY_LAYOUT_ORDER = [
  FormFieldId.DELIVERY_COMPANY,
  FormFieldId.DELIVERY_FIRST_NAME,
  FormFieldId.DELIVERY_LAST_NAME,
  FormFieldId.DELIVERY_STREET_ADDRESS,
  FormFieldId.DELIVERY_POSTAL_CODE,
  FormFieldId.DELIVERY_CITY,
  FormFieldId.DELIVERY_PHONE,
  FormFieldId.DELIVERY_COUNTRY,
  FormFieldId.DELIVERY_CARE_OF,
];

const DELIVERY_FIELD_WIDTH_CLASS = {
  [FormFieldId.DELIVERY_STREET_ADDRESS]: 'full',
  [FormFieldId.DELIVERY_CARE_OF]: 'full',
};

/** Backend may omit company from customer_fields while DELIVERY_IS_COMPANY is false. */
function getOrderDeliveryCustomerLine(order, fieldId) {
  const fromList = order?.deliveryFields?.find((f) => f.id === fieldId);
  if (fromList) return fromList;
  return order?.customer_fields?.get(fieldId) ?? null;
}

function syntheticDraftCompanyFieldMeta(fieldId) {
  if (fieldId === FormFieldId.DELIVERY_COMPANY) {
    return { required: true, max_length: null, min_length: null };
  }
  return null;
}

/**
 * @param {boolean} draftCompanyUi — user turned on company in this modal while order may still be consumer.
 */
function resolveDraftDeliveryFieldMeta(
  deliveryForm,
  order,
  fieldId,
  draftCompanyUi
) {
  const formField = deliveryForm.getField(fieldId);
  if (formField) return formField;
  const fromOrder = getOrderDeliveryCustomerLine(order, fieldId);
  if (fromOrder) return fromOrder;
  if (draftCompanyUi) {
    return syntheticDraftCompanyFieldMeta(fieldId);
  }
  return null;
}

/** MobX form field or order line; plain synthetic meta has no `active`. */
function draftLayoutFieldIsActive(meta) {
  if (!meta) return false;
  const a = meta.active;
  if (typeof a === 'boolean') return a;
  return true;
}

const DraftFloatingTextInput = ({
  label,
  required,
  value,
  onValueChange,
  error,
  disabled,
  type = 'text',
  maxLength,
  minLength,
  description,
}) => {
  const id = useMemo(() => uniqueId('draft-field-'), []);
  const hasValue = String(value ?? '').trim() !== '';
  return (
    <FormGroup
      className={classNames(
        'FormGroupField',
        'FormGroupField--floating',
        hasValue && 'FormGroupField--has-value'
      )}
    >
      <FloatingLabelWrapper
        baseClass="FormGroupField"
        id={id}
        label={label}
        required={required}
      >
        <Input
          id={id}
          type={type}
          value={value ?? ''}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
        />
      </FloatingLabelWrapper>
      {description}
      {error ? (
        <FormFeedback className="FieldError">{error}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

const DraftFloatingSelect = ({
  label,
  required,
  value,
  onValueChange,
  options,
  error,
  disabled,
}) => {
  const id = useMemo(() => uniqueId('draft-select-'), []);

  /** Match CheckoutDeliveryForm / Field: controlled value must match an option or the label and first option overlap. */
  const resolvedValue = useMemo(() => {
    const s = String(value ?? '').trim();
    if (!options.length) return '';
    if (options.some((o) => o.value === s)) return s;
    return options[0].value;
  }, [value, options]);

  useLayoutEffect(() => {
    if (!options.length) return;
    const s = String(value ?? '').trim();
    if (!options.some((o) => o.value === s)) {
      onValueChange(options[0].value);
    }
  }, [value, options, onValueChange]);

  const hasValue = String(resolvedValue).trim() !== '';
  return (
    <FormGroup
      className={classNames(
        'SelectField',
        'SelectField--floating',
        hasValue && 'SelectField--has-value'
      )}
      tag="fieldset"
    >
      <legend className="sr-only">{label}</legend>
      <FloatingLabelWrapper
        baseClass="SelectField"
        id={id}
        label={label}
        required={required}
      >
        <Input
          id={id}
          type="select"
          value={resolvedValue}
          onChange={(e) => onValueChange(e.target.value)}
          disabled={disabled}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Input>
      </FloatingLabelWrapper>
      {error ? (
        <FormFeedback className="FieldError">{error}</FormFeedback>
      ) : null}
    </FormGroup>
  );
};

/**
 * Local-only delivery form for "Create new address" when the order is still tied to the address book
 * (`ONE_TIME_DELIVERY_ADDRESS` false). Does not write to `order.customer_fields` until Save.
 */
const RecipientDeliveryDraftForm = ({
  checkoutStore,
  deliveryForm,
  draft,
  onDraftFieldChange,
  onDeliveryIsCompanyChange,
  onSaveToAddressBookChange,
  fieldErrors,
  sectionTitle,
  showSaveToAddressBookSwitch,
}) => {
  const { order, shippingInfoIsLoading } = checkoutStore;
  const disabled = shippingInfoIsLoading;
  const deliveryFormLabels = getDeliveryFormLabels();

  const careOfHasDraft = draft.careOf && String(draft.careOf).trim() !== '';
  const [showCareOfField, setShowCareOfField] = useState(careOfHasDraft);

  const deliveryIsCompanyCustomerField = order.customer_fields?.get(
    FormFieldId.DELIVERY_IS_COMPANY
  );
  const showDeliveryIsCompanyToggle = Boolean(deliveryIsCompanyCustomerField);
  const isDeliveryCompanyCheckout = Boolean(draft.deliveryIsCompany);

  const oneTimeDeliveryField = order.getCustomerField(
    FormFieldId.ONE_TIME_DELIVERY_ADDRESS
  ).field;

  const setDraftField = (fieldId, raw) => {
    onDraftFieldChange(fieldId, raw);
  };

  return (
    <div className="CheckoutDeliveryForm">
      <div className="CheckoutDeliveryForm__title">
        {sectionTitle != null ? (
          sectionTitle
        ) : (
          <FormattedMessage {...globalTranslations.recipientInformationTitle} />
        )}
      </div>
      <div className="CheckoutDeliveryForm__form-inner">
        {showDeliveryIsCompanyToggle ? (
          <Row>
            <Col>
              <div className="CheckoutDeliveryForm__toggle-row">
                <Switch
                  checked={isDeliveryCompanyCheckout}
                  onChange={(e) =>
                    onDeliveryIsCompanyChange(!!e.target.checked)
                  }
                  size="xs"
                  disabled={disabled}
                >
                  <FormattedMessage
                    {...globalTranslations.recipientIsCompanyLabel}
                  />
                </Switch>
              </div>
            </Col>
          </Row>
        ) : null}
        {deliveryForm.hasFields &&
          (() => {
            const deliveryFormFields = deliveryForm.getFormFields(
              new Set([FormFieldType.BOOLEAN, FormFieldType.BUTTON])
            );
            const draftCompanyUi =
              showDeliveryIsCompanyToggle && draft.deliveryIsCompany;

            const availableIds = new Set(
              deliveryFormFields
                .filter((f) => {
                  if (
                    showDeliveryIsCompanyToggle &&
                    f.id === FormFieldId.DELIVERY_COMPANY
                  ) {
                    return draft.deliveryIsCompany;
                  }
                  return f.active;
                })
                .map((f) => f.id)
            );

            if (draftCompanyUi) {
              availableIds.add(FormFieldId.DELIVERY_COMPANY);
            }

            // B2C + address dropdown: deliveryForm only contains the dropdown; address
            // lines still live on order.deliveryFields (see buildB2CForms).
            for (const df of order.deliveryFields || []) {
              if (df.id === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) continue;
              if (
                df.id === FormFieldId.DELIVERY_COMPANY &&
                showDeliveryIsCompanyToggle &&
                !draft.deliveryIsCompany
              ) {
                continue;
              }
              if (draftLayoutFieldIsActive(df)) {
                availableIds.add(df.id);
              }
            }

            const renderDeliveryCompanyRow = () => {
              const companyFormField = deliveryForm.getField(
                FormFieldId.DELIVERY_COMPANY
              );
              const companyMeta = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_COMPANY,
                draftCompanyUi
              );
              const showCompanyInput =
                companyMeta &&
                (showDeliveryIsCompanyToggle
                  ? draft.deliveryIsCompany
                  : Boolean(companyFormField?.active));
              if (!showCompanyInput) return null;
              return (
                <div
                  className="CheckoutDeliveryForm__field-row CheckoutDeliveryForm__field-row--company"
                  key="delivery-company-row"
                >
                  <div className="CheckoutDeliveryForm__field-row__company">
                    <DraftFloatingTextInput
                      label={deliveryFormLabels[FormFieldId.DELIVERY_COMPANY]}
                      required={companyMeta.required}
                      value={draft.company}
                      onValueChange={(v) =>
                        setDraftField(FormFieldId.DELIVERY_COMPANY, v)
                      }
                      error={fieldErrors[FormFieldId.DELIVERY_COMPANY]}
                      disabled={disabled}
                      maxLength={companyMeta.max_length}
                      minLength={companyMeta.min_length}
                    />
                  </div>
                </div>
              );
            };

            const renderDeliveryNameRow = () => {
              const firstNameField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_FIRST_NAME,
                draftCompanyUi
              );
              const lastNameField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_LAST_NAME,
                draftCompanyUi
              );
              const phoneField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_PHONE,
                draftCompanyUi
              );
              if (
                !draftLayoutFieldIsActive(firstNameField) &&
                !draftLayoutFieldIsActive(lastNameField) &&
                !draftLayoutFieldIsActive(phoneField)
              ) {
                return null;
              }
              return (
                <div
                  className="CheckoutDeliveryForm__field-row CheckoutDeliveryForm__field-row--name"
                  key="delivery-name-row"
                >
                  {draftLayoutFieldIsActive(firstNameField) && (
                    <div className="CheckoutDeliveryForm__field-row__first-name">
                      <DraftFloatingTextInput
                        label={labels.firstName}
                        required={
                          firstNameField.required && !isDeliveryCompanyCheckout
                        }
                        value={draft.firstname}
                        onValueChange={(v) =>
                          setDraftField(FormFieldId.DELIVERY_FIRST_NAME, v)
                        }
                        error={fieldErrors[FormFieldId.DELIVERY_FIRST_NAME]}
                        disabled={disabled}
                        maxLength={firstNameField.max_length}
                        minLength={firstNameField.min_length}
                      />
                    </div>
                  )}
                  {draftLayoutFieldIsActive(lastNameField) && (
                    <div className="CheckoutDeliveryForm__field-row__last-name">
                      <DraftFloatingTextInput
                        label={
                          deliveryFormLabels[FormFieldId.DELIVERY_LAST_NAME]
                        }
                        required={
                          lastNameField.required && !isDeliveryCompanyCheckout
                        }
                        value={draft.lastname}
                        onValueChange={(v) =>
                          setDraftField(FormFieldId.DELIVERY_LAST_NAME, v)
                        }
                        error={fieldErrors[FormFieldId.DELIVERY_LAST_NAME]}
                        disabled={disabled}
                        maxLength={lastNameField.max_length}
                        minLength={lastNameField.min_length}
                      />
                    </div>
                  )}
                  {draftLayoutFieldIsActive(phoneField) && (
                    <div className="CheckoutDeliveryForm__field-row__phone">
                      <DraftFloatingTextInput
                        label={deliveryFormLabels[FormFieldId.DELIVERY_PHONE]}
                        required={phoneField.required}
                        value={draft.telephone}
                        onValueChange={(v) =>
                          setDraftField(FormFieldId.DELIVERY_PHONE, v)
                        }
                        error={fieldErrors[FormFieldId.DELIVERY_PHONE]}
                        disabled={disabled}
                        type="tel"
                        maxLength={phoneField.max_length}
                        minLength={phoneField.min_length}
                        description={
                          <small className="form-text CheckoutDeliveryForm__phone-description">
                            <FormattedMessage {...messages.phonePlaceholder} />
                          </small>
                        }
                      />
                    </div>
                  )}
                </div>
              );
            };

            const renderDeliveryPostalRow = () => {
              const postalField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_POSTAL_CODE,
                draftCompanyUi
              );
              const cityField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_CITY,
                draftCompanyUi
              );
              const countryField = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_COUNTRY,
                draftCompanyUi
              );
              if (
                !draftLayoutFieldIsActive(postalField) &&
                !draftLayoutFieldIsActive(cityField) &&
                !draftLayoutFieldIsActive(countryField)
              ) {
                return null;
              }
              const hasCountry = draftLayoutFieldIsActive(countryField);
              const countrySelectOptions =
                countryField?.type === FormFieldType.SELECT
                  ? (countryField.options || [])
                      .filter((o) => String(o.value ?? '').trim() !== '')
                      .map((o) => ({
                        value: String(o.value),
                        label: o.label,
                      }))
                  : [];
              return (
                <div
                  className={classNames(
                    'CheckoutDeliveryForm__field-row',
                    'CheckoutDeliveryForm__field-row--postal',
                    {
                      'CheckoutDeliveryForm__field-row--postal-no-country':
                        !hasCountry,
                    }
                  )}
                  key="delivery-postal-row"
                >
                  {draftLayoutFieldIsActive(postalField) && (
                    <div className="CheckoutDeliveryForm__field-row__postal-code">
                      <DraftFloatingTextInput
                        label={
                          deliveryFormLabels[FormFieldId.DELIVERY_POSTAL_CODE]
                        }
                        required={postalField.required}
                        value={draft.postalCode}
                        onValueChange={(v) =>
                          setDraftField(FormFieldId.DELIVERY_POSTAL_CODE, v)
                        }
                        error={fieldErrors[FormFieldId.DELIVERY_POSTAL_CODE]}
                        disabled={disabled}
                        maxLength={postalField.max_length}
                        minLength={postalField.min_length}
                      />
                    </div>
                  )}
                  {draftLayoutFieldIsActive(cityField) && (
                    <div className="CheckoutDeliveryForm__field-row__city">
                      <DraftFloatingTextInput
                        label={deliveryFormLabels[FormFieldId.DELIVERY_CITY]}
                        required={cityField.required}
                        value={draft.city}
                        onValueChange={(v) =>
                          setDraftField(FormFieldId.DELIVERY_CITY, v)
                        }
                        error={fieldErrors[FormFieldId.DELIVERY_CITY]}
                        disabled={disabled}
                        maxLength={cityField.max_length}
                        minLength={cityField.min_length}
                      />
                    </div>
                  )}
                  {draftLayoutFieldIsActive(countryField) &&
                    (countryField.type === FormFieldType.SELECT ? (
                      <div className="CheckoutDeliveryForm__field-row__country">
                        <DraftFloatingSelect
                          label={
                            deliveryFormLabels[FormFieldId.DELIVERY_COUNTRY]
                          }
                          required={countryField.required}
                          value={draft.country}
                          onValueChange={(v) =>
                            setDraftField(FormFieldId.DELIVERY_COUNTRY, v)
                          }
                          options={countrySelectOptions}
                          error={fieldErrors[FormFieldId.DELIVERY_COUNTRY]}
                          disabled={disabled}
                        />
                      </div>
                    ) : (
                      <div className="CheckoutDeliveryForm__field-row__country">
                        <DraftFloatingTextInput
                          label={
                            deliveryFormLabels[FormFieldId.DELIVERY_COUNTRY]
                          }
                          required={countryField.required}
                          value={draft.country}
                          onValueChange={(v) =>
                            setDraftField(FormFieldId.DELIVERY_COUNTRY, v)
                          }
                          error={fieldErrors[FormFieldId.DELIVERY_COUNTRY]}
                          disabled={disabled}
                          maxLength={countryField.max_length}
                          minLength={countryField.min_length}
                        />
                      </div>
                    ))}
                </div>
              );
            };

            const renderCareOf = (widthClass) => {
              const field = resolveDraftDeliveryFieldMeta(
                deliveryForm,
                order,
                FormFieldId.DELIVERY_CARE_OF,
                draftCompanyUi
              );
              if (!draftLayoutFieldIsActive(field)) return null;
              const hasValue =
                draft.careOf && String(draft.careOf).trim() !== '';
              const shouldShowField = showCareOfField || hasValue;
              if (!shouldShowField) {
                return (
                  <div
                    className={`CheckoutDeliveryForm__field CheckoutDeliveryForm__field--${widthClass}`}
                    key={FormFieldId.DELIVERY_CARE_OF}
                  >
                    <div className="CheckoutDeliveryForm__care-of-link">
                      <button
                        type="button"
                        onClick={() => setShowCareOfField(true)}
                        className="CheckoutDeliveryForm__add-care-of-link btn btn-link p-0 border-0 align-baseline"
                      >
                        {labels.addCareOf}
                      </button>
                    </div>
                  </div>
                );
              }
              return (
                <div
                  className={`CheckoutDeliveryForm__field CheckoutDeliveryForm__field--${widthClass}`}
                  key={FormFieldId.DELIVERY_CARE_OF}
                >
                  <DraftFloatingTextInput
                    label={deliveryFormLabels[FormFieldId.DELIVERY_CARE_OF]}
                    required={field.required}
                    value={draft.careOf}
                    onValueChange={(v) =>
                      setDraftField(FormFieldId.DELIVERY_CARE_OF, v)
                    }
                    error={fieldErrors[FormFieldId.DELIVERY_CARE_OF]}
                    disabled={disabled}
                    maxLength={field.max_length}
                    minLength={field.min_length}
                  />
                </div>
              );
            };

            const deliveryNodes = (() => {
              const nodes = [];
              let skipNextLastName = false;
              let skipNextCity = false;
              let skipNextPhone = false;
              let skipNextCountry = false;
              for (const fieldId of DELIVERY_LAYOUT_ORDER) {
                if (!availableIds.has(fieldId)) continue;
                if (
                  fieldId === FormFieldId.DELIVERY_LAST_NAME &&
                  skipNextLastName
                ) {
                  skipNextLastName = false;
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_CITY && skipNextCity) {
                  skipNextCity = false;
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_PHONE && skipNextPhone) {
                  skipNextPhone = false;
                  continue;
                }
                if (
                  fieldId === FormFieldId.DELIVERY_COUNTRY &&
                  skipNextCountry
                ) {
                  skipNextCountry = false;
                  continue;
                }
                if (
                  fieldId === FormFieldId.DELIVERY_COMPANY &&
                  availableIds.has(FormFieldId.DELIVERY_COMPANY)
                ) {
                  nodes.push({ type: 'company-row' });
                  continue;
                }
                if (fieldId === FormFieldId.DELIVERY_FIRST_NAME) {
                  if (
                    availableIds.has(FormFieldId.DELIVERY_LAST_NAME) &&
                    availableIds.has(FormFieldId.DELIVERY_PHONE)
                  ) {
                    nodes.push({ type: 'name-row' });
                    skipNextLastName = true;
                    skipNextPhone = true;
                    continue;
                  }
                }
                if (
                  fieldId === FormFieldId.DELIVERY_POSTAL_CODE &&
                  availableIds.has(FormFieldId.DELIVERY_CITY)
                ) {
                  nodes.push({ type: 'postal-row' });
                  skipNextCity = true;
                  if (availableIds.has(FormFieldId.DELIVERY_COUNTRY)) {
                    skipNextCountry = true;
                  }
                  continue;
                }
                nodes.push({ type: 'field', fieldId });
              }
              return nodes;
            })();

            return (
              <div className="CheckoutDeliveryForm__fields">
                {deliveryNodes.map((node) => {
                  if (node.type === 'company-row') {
                    return (
                      <React.Fragment key="delivery-company-row">
                        {renderDeliveryCompanyRow()}
                      </React.Fragment>
                    );
                  }
                  if (node.type === 'name-row') {
                    const nameRow = renderDeliveryNameRow();
                    if (!nameRow) return null;
                    if (isDeliveryCompanyCheckout) {
                      return (
                        <div
                          className="CheckoutDeliveryForm__contact-section"
                          key="delivery-name-row"
                        >
                          <div className="CheckoutDeliveryForm__contact-heading">
                            <FormattedMessage
                              {...globalTranslations.contactSectionTitle}
                            />
                          </div>
                          {nameRow}
                        </div>
                      );
                    }
                    return (
                      <React.Fragment key="delivery-name-row">
                        {nameRow}
                      </React.Fragment>
                    );
                  }
                  if (node.type === 'postal-row') {
                    return (
                      <React.Fragment key="delivery-postal-row">
                        {renderDeliveryPostalRow()}
                      </React.Fragment>
                    );
                  }
                  if (node.fieldId === FormFieldId.DELIVERY_CARE_OF) {
                    const widthClass =
                      DELIVERY_FIELD_WIDTH_CLASS[node.fieldId] || 'full';
                    return (
                      <React.Fragment key={node.fieldId}>
                        {renderCareOf(widthClass)}
                      </React.Fragment>
                    );
                  }
                  const field = resolveDraftDeliveryFieldMeta(
                    deliveryForm,
                    order,
                    node.fieldId,
                    draftCompanyUi
                  );
                  if (!field) return null;
                  const widthClass =
                    DELIVERY_FIELD_WIDTH_CLASS[node.fieldId] || 'full';
                  const val = getManualDraftFieldValue(draft, node.fieldId);
                  return (
                    <div
                      className={`CheckoutDeliveryForm__field CheckoutDeliveryForm__field--${widthClass}`}
                      key={node.fieldId}
                    >
                      <DraftFloatingTextInput
                        label={deliveryFormLabels[node.fieldId]}
                        required={field.required}
                        value={val}
                        onValueChange={(v) => setDraftField(node.fieldId, v)}
                        error={fieldErrors[node.fieldId]}
                        disabled={disabled}
                        maxLength={field.max_length}
                        minLength={field.min_length}
                      />
                    </div>
                  );
                })}
                {[...availableIds]
                  .filter((id) => !DELIVERY_LAYOUT_ORDER.includes(id))
                  .map((fieldId) => {
                    if (fieldId === FormFieldId.DELIVERY_ADDRESS_DROPDOWN) {
                      return null;
                    }
                    const field = resolveDraftDeliveryFieldMeta(
                      deliveryForm,
                      order,
                      fieldId,
                      draftCompanyUi
                    );
                    if (!field) return null;
                    const val = getManualDraftFieldValue(draft, fieldId);
                    return (
                      <div
                        className="CheckoutDeliveryForm__field CheckoutDeliveryForm__field--full"
                        key={fieldId}
                      >
                        <DraftFloatingTextInput
                          label={
                            deliveryFormLabels[fieldId] ?? labels.streetAddress
                          }
                          required={field.required}
                          value={val}
                          onValueChange={(v) => setDraftField(fieldId, v)}
                          error={fieldErrors[fieldId]}
                          disabled={disabled}
                          maxLength={field.max_length}
                          minLength={field.min_length}
                        />
                      </div>
                    );
                  })}
              </div>
            );
          })()}
        {showSaveToAddressBookSwitch && oneTimeDeliveryField ? (
          <Row>
            <Col>
              <div className="CheckoutDeliveryForm__toggle-row CheckoutDeliveryForm__save-to-address-book-row">
                <Switch
                  checked={draft.saveToAddressBook}
                  onChange={(e) =>
                    onSaveToAddressBookChange(!!e.target.checked)
                  }
                  size="xs"
                  disabled={disabled}
                >
                  <FormattedMessage
                    {...globalTranslations.saveAddressForLaterUse}
                  />
                </Switch>
              </div>
            </Col>
          </Row>
        ) : null}
      </div>
    </div>
  );
};

RecipientDeliveryDraftForm.propTypes = {
  checkoutStore: modelOf(CheckoutStore).isRequired,
  deliveryForm: modelOf(FormModel).isRequired,
  draft: PropTypes.object.isRequired,
  onDraftFieldChange: PropTypes.func.isRequired,
  onDeliveryIsCompanyChange: PropTypes.func.isRequired,
  onSaveToAddressBookChange: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object,
  sectionTitle: PropTypes.node,
  showSaveToAddressBookSwitch: PropTypes.bool,
};

RecipientDeliveryDraftForm.defaultProps = {
  fieldErrors: {},
  sectionTitle: null,
  showSaveToAddressBookSwitch: false,
};

export default inject('checkoutStore')(observer(RecipientDeliveryDraftForm));
